import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import Spinner from "../../primitives/Spinner/Spinner";
import { ButtonGroupContext } from "./ButtonGroup";
import { colorOf, sizeOf } from "../../utils/propTypes";
import css from "./Button.module.css";

const Button = React.forwardRef(
  (
    {
      variant: variantProp,
      size: sizeProp,
      color = "primary",
      disabled = false,
      loading = false,
      fullWidth = false,
      leading,
      trailing,
      as: Component = "button",
      className,
      children,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const group = React.useContext(ButtonGroupContext);
    const size = sizeProp || group?.size || "md";
    const variant = variantProp || group?.variant || "filled";
    const isDisabled = disabled || loading;

    const classes = clsx(
      css.root,
      css[size],
      css[variant],
      css[`color-${color}`],
      fullWidth && css.fullWidth,
      loading && css.loading,
      className,
    );

    const handleClick = (e) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      onClick?.(e);
    };

    const isLink = rest.href !== undefined || rest.to !== undefined;

    const buttonProps =
      Component === "button"
        ? { type: rest.type || "button", disabled: isDisabled }
        : {
            role: isLink ? undefined : "button",
            "aria-disabled": isDisabled || undefined,
          };

    return (
      <Component
        ref={ref}
        className={classes}
        onClick={handleClick}
        aria-busy={loading || undefined}
        {...buttonProps}
        {...rest}
      >
        {leading && <span className={css.icon}>{leading}</span>}
        {loading ? <span>{children}</span> : children}
        {trailing && <span className={css.icon}>{trailing}</span>}
        {loading && (
          <span className={css.spinner}>
            <Spinner size={size === "lg" ? "md" : "sm"} label="Loading…" />
          </span>
        )}
      </Component>
    );
  },
);

Button.displayName = "Button";

Button.propTypes = {
  variant: PropTypes.oneOf(["filled", "tonal", "outlined", "text", "elevated"]),
  size: sizeOf("xs", "sm", "md", "lg"),
  color: colorOf("primary", "error", "success"),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  leading: PropTypes.node,
  trailing: PropTypes.node,
  as: PropTypes.elementType,
  className: PropTypes.string,
  children: PropTypes.node,
  onClick: PropTypes.func,
};

export default Button;
